import { useCallback, useEffect, useRef, useState } from "react";

import { t } from "../i18n/strings";
import { tapFeedback } from "../telegram/sdk";
import { copyText } from "../utils/clipboard";

/** How long the "скопировано" bubble stays up. Long enough to read, short enough not to nag. */
export const FLASH_MS = 1400;

interface CopyIdProps {
  value: number | string;
}

/**
 * A telegram id that copies itself when tapped.
 *
 * An admin needs the id in another chat or in another tab of this application far more often
 * than they need to look at it, and selecting a number inside a mini app is a fight with the
 * webview's own gestures. So the id is a button, and it says whether the copy worked: telegram
 * may refuse the clipboard, and a button that quietly does nothing is worse than no button.
 *
 * Clicks stop here on purpose - the ticket card around this one opens the request.
 */
export function CopyId({ value }: CopyIdProps) {
  const [flash, setFlash] = useState<"copied" | "failed" | null>(null);
  const timer = useRef<number | undefined>(undefined);

  // the bubble is a timeout, and a timeout that outlives the component sets state on nothing
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const copied = await copyText(String(value));
      if (copied) {
        tapFeedback();
      }
      setFlash(copied ? "copied" : "failed");
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setFlash(null), FLASH_MS);
    },
    [value],
  );

  return (
    <span className="copy-id">
      <button
        type="button"
        className="copy-id__button"
        title={t("copy.hint")}
        aria-label={t("copy.aria", { value })}
        onClick={(event) => void copy(event)}
      >
        <span className="copy-id__value">{value}</span>
        <span className="copy-id__icon" aria-hidden="true">
          ⧉
        </span>
      </button>

      <span className={`copy-id__flash${flash === "failed" ? " copy-id__flash--failed" : ""}`} role="status">
        {flash === null ? "" : t(flash === "copied" ? "copy.done" : "copy.failed")}
      </span>
    </span>
  );
}
